/* eslint-disable */
/**
 * @fileoverview Login Page Component for Clockwise CPA
 *
 * Main orchestrator component that handles user authentication including login,
 * password reset, and two-factor authentication. This is the refactored version
 * that delegates to smaller, focused components and hooks.
 *
 * Key Features:
 * - User login with email/username and password
 * - Password reset functionality with email verification
 * - Two-factor authentication (2FA) support
 * - Form validation using Formik and Yup
 * - Responsive design with mobile support
 * - Biometric/passkey authentication
 * - Quick login for recent users
 *
 * @author Clockwise CPA Development Team
 * @version 2.0.0
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';

// Context
import { useAuth } from '../../context/AuthContext';
import { useNotify } from '../../context/NotificationContext';
import { useUtils } from '../../context/UtilContext';

// Components
import DevOrganizationSwitcher from '../../components/DevOrganizationSwitcher';
import DarkModeToggle from '../../components/DarkModeToggle';
import BiometricSetupPrompt from '../../components/BiometricSetupPrompt';
import CompTimeWarningPrompt from '../../components/CompTimeWarningPrompt';

// Local Components
import {
  LoginForm,
  TwoFactorForm,
  LogoutOverlay,
  BrandingPanel,
  SessionExpiredModal,
  LoginErrorModal,
  WebAuthnUnsupportedModal,
  ResetPasswordModal
} from './components';

// Local Hooks
import { useLoginForm, useBiometricAuth, use2FAFlow } from './hooks';

// Utils
import { getRecentUsers, removeRecentUser, addRecentUser } from './utils/recentUsers';
import { SEASONAL_THEMES } from './utils/constants';
import { isWebAuthnSupported } from '../../utils/webauthn';

// Styles
import '../../styles/Login.css';

/**
 * Login Page Component
 *
 * Renders the login page with authentication forms, password reset functionality,
 * and two-factor authentication support. Handles all user authentication flows.
 *
 * @component
 * @returns {JSX.Element} The login page component
 */
const LoginPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showLoginTransition } = useUtils();
  const { error, spinner } = useNotify();
  const {
    getUser,
    getCookie,
    requestPasswordReset,
    isLoggingOut,
    APP_URL
  } = useAuth();

  // Modal states
  const [showResetPasswordForm, setShowResetPasswordForm] = useState(false);
  const [showBiometricPrompt, setShowBiometricPrompt] = useState(false);
  const [showWebAuthnUnsupportedModal, setShowWebAuthnUnsupportedModal] = useState(false);
  const [biometricPromptUsername, setBiometricPromptUsername] = useState('');
  const [showCompTimeWarning, setShowCompTimeWarning] = useState(false);
  const [compTimeBalance, setCompTimeBalance] = useState(0);
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [logoutComplete, setLogoutComplete] = useState(false);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);

  // Error modal state
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalConfig, setErrorModalConfig] = useState({
    title: 'Error',
    message: '',
    icon: null,
    type: 'error'
  });

  // Recent users state
  const [recentUsers, setRecentUsers] = useState([]);

  // Track passkey prompt dismissed state
  const [passkeyPromptDismissed, setPasskeyPromptDismissed] = useState(false);

  /**
   * Show error modal with appropriate styling based on error type
   */
  const showLoginErrorModal = (errorMessage, isModal = false) => {
    let config = {
      title: 'Login Failed',
      message: errorMessage || 'An error occurred during login.',
      icon: null,
      type: 'error'
    };

    if (errorMessage?.toLowerCase().includes('locked')) {
      config = {
        title: 'Account Locked',
        message: 'Your account has been locked due to security reasons. Please contact an administrator to unlock your account.',
        icon: null,
        type: 'locked'
      };
    } else if (errorMessage?.toLowerCase().includes('deactivated') || errorMessage?.toLowerCase().includes('inactive')) {
      config = {
        title: 'Account Deactivated',
        message: 'Your account has been deactivated. Please contact support for assistance.',
        icon: null,
        type: 'deactivated'
      };
    }

    setErrorModalConfig(config);
    setShowErrorModal(true);
  };

  /**
   * Check comp time balance for warnings
   */
  const checkCompTimeBalance = async (username) => {
    try {
      const response = await fetch(`${APP_URL}/api/time-banks`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.comp_balance >= 80) {
          setCompTimeBalance(data.comp_balance);
          return true;
        }
      }
      return false;
    } catch (err) {
      console.error('Error checking comp time balance:', err);
      return false;
    }
  };

  /**
   * Dismiss passkey prompt on server
   */
  const dismissPasskeyPrompt = async () => {
    try {
      const csrfToken = getCookie('csrf_access_token');
      await fetch(`${APP_URL}/user`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken
        },
        body: JSON.stringify({ action: 'dismiss_passkey_prompt' })
      });
    } catch (err) {
      console.error('Failed to dismiss passkey prompt:', err);
    }
  };

  // 2FA Flow Hook
  const twoFactorFlow = use2FAFlow({
    onSuccess: async ({ username, hasPasskey, needsPasskeySetup, passkeyPromptDismissed: dismissed }) => {
      setPasskeyPromptDismissed(dismissed);

      const hasCompTimeWarning = await checkCompTimeBalance(username);

      await showLoginTransition({
        message: 'Welcome back!',
        subMessage: 'Signing you in to Clockwise...',
        onFadeStart: () => {
          if (hasCompTimeWarning) {
            setBiometricPromptUsername(username);
            setShowCompTimeWarning(true);
          } else if (!dismissed) {
            setBiometricPromptUsername(username);
            if (isWebAuthnSupported()) {
              setShowBiometricPrompt(true);
            } else {
              setShowWebAuthnUnsupportedModal(true);
            }
          } else {
            navigate('/');
          }
        }
      });
    }
  });

  // Login Form Hook
  const loginForm = useLoginForm({
    onRequires2FA: (params) => {
      twoFactorFlow.initiate2FA(params);
    },
    onLoginSuccess: async ({ username, user }) => {
      const hasCompTimeWarning = await checkCompTimeBalance(username);

      if (hasCompTimeWarning) {
        setBiometricPromptUsername(username);
        setShowCompTimeWarning(true);
      } else {
        navigate('/');
      }
    },
    onError: showLoginErrorModal
  });

  // Biometric Auth Hook
  const biometricAuth = useBiometricAuth({
    onSuccess: async ({ username, user, isQuickLogin }) => {
      const hasCompTimeWarning = await checkCompTimeBalance(username);

      await showLoginTransition({
        message: 'Welcome Back!',
        subMessage: 'Authentication successful. Signing you in...',
        onFadeStart: () => {
          if (hasCompTimeWarning) {
            setBiometricPromptUsername(username);
            setShowCompTimeWarning(true);
          } else {
            navigate('/');
          }
        }
      });
    },
    onError: showLoginErrorModal,
    setRecentUsers
  });

  // Reset Password Formik
  const resetPasswordFormik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: Yup.object({
      email: Yup.string().email('Invalid email address').required('Email is required'),
    }),
    onSubmit: async (values) => {
      setResetPasswordLoading(true);
      const { complete } = spinner("Sending password reset email");

      try {
        const result = await requestPasswordReset(values.email);

        if (result.success) {
          complete(true, "Password reset email sent!");
          setTimeout(() => {
            setShowResetPasswordForm(false);
          }, 2000);
        } else {
          complete(false);
          error(result.error || 'Failed to send reset email');
        }
      } catch (err) {
        complete(false);
        error('An unexpected error occurred');
      } finally {
        setResetPasswordLoading(false);
      }
    },
  });

  // Remove seasonal themes on login page
  useEffect(() => {
    document.documentElement.classList.remove(...SEASONAL_THEMES);
  }, []);

  // Show session expired modal if coming from expired session
  useEffect(() => {
    const expired = searchParams.get('expired');
    if (expired === 'true') {
      setShowSessionExpiredModal(true);
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('expired');
      navigate({ search: newSearchParams.toString() }, { replace: true });
    }
  }, []);

  // Check if user is already logged in
  useEffect(() => {
    const checkExistingAuth = async () => {
      try {
        if (isLoggingOut) {
          return;
        }

        // Don't check auth if we just came from session expired - cookies are invalid
        const expired = searchParams.get('expired');
        if (expired === 'true') {
          return;
        }

        if (getCookie('csrf_access_token')) {
          const userData = await getUser({ cache: false });
          if (userData) {
            console.log('User already authenticated, redirecting');
            navigate('/');
          }
        }
      } catch (err) {
        console.log('No existing authentication found');
      }
    };

    checkExistingAuth();
  }, [isLoggingOut]);

  // Show logout animation when logging out
  useEffect(() => {
    if (isLoggingOut) {
      setShowLogoutModal(true);
      setLogoutComplete(false);
    } else if (showLogoutModal) {
      setLogoutComplete(true);
      const timer = setTimeout(() => {
        setShowLogoutModal(false);
        setLogoutComplete(false);
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [isLoggingOut, showLogoutModal]);

  // Test comp time warning from dev panel
  useEffect(() => {
    const handleTestComptimeWarning = (event) => {
      const { compBalance } = event.detail;
      setCompTimeBalance(compBalance);
      setShowCompTimeWarning(true);
      setBiometricPromptUsername('test-user');
    };

    window.addEventListener('test-comptime-warning', handleTestComptimeWarning);
    return () => {
      window.removeEventListener('test-comptime-warning', handleTestComptimeWarning);
    };
  }, []);

  // Load recent users on mount
  useEffect(() => {
    setRecentUsers(getRecentUsers());
  }, []);

  // Handlers
  const handleRemoveRecentUser = (e, username) => {
    e.stopPropagation();
    removeRecentUser(username);
    setRecentUsers(getRecentUsers());
  };

  const handleSelectRecentUser = (recentUser) => {
    biometricAuth.handleSelectRecentUser(recentUser, loginForm.formik.setFieldValue);
  };

  const handleBiometricPromptSetup = async () => {
    await dismissPasskeyPrompt();
    setShowBiometricPrompt(false);
    navigate('/settings?tab=security&openBiometric=true');
  };

  const handleBiometricPromptDismiss = async () => {
    await dismissPasskeyPrompt();
    setShowBiometricPrompt(false);
    navigate('/');
  };

  const handleWebAuthnUnsupportedDismiss = async () => {
    await dismissPasskeyPrompt();
    setShowWebAuthnUnsupportedModal(false);
    navigate('/');
  };

  const handleCompTimeWarningClose = () => {
    setShowCompTimeWarning(false);
    navigate('/');
  };

  return (
    <div className="lp-container">
      {/* Development Organization Switcher */}
      <DevOrganizationSwitcher />

      {/* Branding Panel (Left Side) */}
      <BrandingPanel />

      {/* Login Panel (Right Side) */}
      <div className="lp-login-panel">
        {/* Theme Toggle */}
        <div className="lp-theme-toggle">
          <DarkModeToggle disableSeasonalThemes />
        </div>

        <LoginForm
          formik={loginForm.formik}
          loading={loginForm.loading}
          showPassword={loginForm.showPassword}
          onTogglePassword={loginForm.togglePasswordVisibility}
          onForgotPassword={() => setShowResetPasswordForm(true)}
          recentUsers={recentUsers}
          quickLoginUser={biometricAuth.quickLoginUser}
          quickLoginLoading={biometricAuth.quickLoginLoading}
          onSelectRecentUser={handleSelectRecentUser}
          onRemoveRecentUser={handleRemoveRecentUser}
        />
      </div>

      {/* Reset Password Modal */}
      <ResetPasswordModal
        show={showResetPasswordForm}
        formik={resetPasswordFormik}
        loading={resetPasswordLoading}
        onClose={() => setShowResetPasswordForm(false)}
      />

      {/* 2FA Modals */}
      <TwoFactorForm
        show2FAModal={twoFactorFlow.show2FAModal}
        emailCodeRequested={twoFactorFlow.emailCodeRequested}
        loading={twoFactorFlow.loading}
        hasBiometric={twoFactorFlow.hasBiometric}
        hasEmail={twoFactorFlow.hasEmail}
        onBiometric2FA={twoFactorFlow.handleBiometric2FA}
        onRequestEmailCode={twoFactorFlow.handleRequestEmailCode}
        on2FASubmit={twoFactorFlow.handle2FASubmit}
        onBack={twoFactorFlow.goBack2FAChoice}
        onClose={twoFactorFlow.reset2FA}
      />

      {/* Comp Time Warning Prompt */}
      <CompTimeWarningPrompt
        isOpen={showCompTimeWarning}
        compBalance={compTimeBalance}
        onClose={handleCompTimeWarningClose}
      />

      {/* Biometric Setup Prompt */}
      <BiometricSetupPrompt
        isOpen={showBiometricPrompt}
        onSetup={handleBiometricPromptSetup}
        onDismiss={handleBiometricPromptDismiss}
      />

      {/* WebAuthn Unsupported Modal */}
      <WebAuthnUnsupportedModal
        show={showWebAuthnUnsupportedModal}
        onDismiss={handleWebAuthnUnsupportedDismiss}
      />

      {/* Session Expired Modal */}
      <SessionExpiredModal
        show={showSessionExpiredModal}
        onClose={() => setShowSessionExpiredModal(false)}
      />

      {/* Login Error Modal */}
      <LoginErrorModal
        show={showErrorModal}
        config={errorModalConfig}
        onClose={() => setShowErrorModal(false)}
      />

      {/* Logout Animation Overlay - show immediately when isLoggingOut */}
      <LogoutOverlay
        show={isLoggingOut || showLogoutModal}
        complete={logoutComplete}
      />
    </div>
  );
};

export default LoginPage;
